import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { Actor, DataMode } from '@/data/runtime';
import type { Person, Settings } from '@/domain/types';

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

export type ThemePref = 'system' | 'light' | 'dark';
const THEME_KEY = 'lastcall.theme';

function readTheme(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function applyTheme(pref: ThemePref) {
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  // Matches the header (--surface) so the browser bar blends into it.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#161616' : '#ffffff');
}

const ThemeContext = createContext<{ pref: ThemePref; setPref: (p: ThemePref) => void }>({ pref: 'system', setPref: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readTheme);
  useEffect(() => {
    applyTheme(pref);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme(pref);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);
  const setPref = (p: ThemePref) => {
    try {
      localStorage.setItem(THEME_KEY, p);
    } catch {
      /* preference just won't persist */
    }
    setPrefState(p);
  };
  return <ThemeContext.Provider value={{ pref, setPref }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);

// ---------------------------------------------------------------------------
// Online status (only matters for app updates: counting never needs the internet)
// ---------------------------------------------------------------------------

function subscribeOnline(cb: () => void) {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

// ---------------------------------------------------------------------------
// Session: which records (Red's or practice), who is using the tablet, settings
// ---------------------------------------------------------------------------

export interface SessionValue {
  mode: DataMode;
  actor: Actor;
  people: Person[];
  settings: Settings;
  /** Changes who is using the tablet (the PIN, if any, is checked before this is called). */
  switchUser: (userId: string) => void;
  switchMode: (mode: DataMode | null) => void;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionContext');
  return s;
}

export function useSettings(): Settings {
  return useSession().settings;
}
